import { useMemo, useState } from 'react';
import { usePaginatedFilter } from '../../../../common/hooks/usePaginatedFilter';
import {
  useLotTickets,
  useCreateLotTicket,
  useUpdateLotTicket,
  useDeleteLotTicket,
} from '../../../../hooks/useLotTickets';
import { useLots } from '../../../../hooks/useLots';
import { useAllotments } from '../../../../hooks/useAllotments';
import { useDistricts } from '../../../../hooks/useDistricts';
import { useCountries } from '../../../../hooks/useCountries';
import { useProvinces } from '../../../../hooks/useProvinces';
import { useDepartments } from '../../../../hooks/useDepartments';
import { useMaritalStatuses } from '../../../../hooks/useMaritalStatuses';
import { useNationalities } from '../../../../hooks/useNationalities';
import { ITEMS_PER_PAGE, INITIAL_LOT_TICKET_STATE } from '../constants/lotTicketsConstants';
import { filterLotTickets } from '../utils/lotTicketsUtils';
import { validateLotTicket } from '../utils/lotTicketsValidation';

const normalizeLotTicket = (lotTicket) => ({
  ...INITIAL_LOT_TICKET_STATE,
  ...lotTicket,
  uuid_lot: lotTicket?.uuid_lot || '',
  uuid_district: lotTicket?.uuid_district || '',
  uuid_marital_status: lotTicket?.uuid_marital_status || '',
  uuid_nationality: lotTicket?.uuid_nationality || '',
  ticket_number: lotTicket?.ticket_number || '',
  email: lotTicket?.email || '',
  phone_number: lotTicket?.phone_number || '',
  birth_date: lotTicket?.birth_date ? lotTicket.birth_date.slice(0, 10) : '',
});

const buildPayload = (lotTicket) => {
  const { uuid_country, uuid_province, uuid_department, ...payload } = lotTicket;
  return {
    ...payload,
    uuid_lot: lotTicket.uuid_lot || null,
    email: lotTicket.email || null,
    phone_number: lotTicket.phone_number || null,
  };
};

export const useLotTicketsTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedLotTicket, setSelectedLotTicket] = useState(INITIAL_LOT_TICKET_STATE);
  const [errors, setErrors] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [lotTicketToDelete, setLotTicketToDelete] = useState(null);

  const { data: lotTickets = [], isLoading: isLotTicketsLoading } = useLotTickets();
  const { data: lots = [], isLoading: isLotsLoading } = useLots();
  const { data: allotments = [] } = useAllotments();
  const { data: districts = [] } = useDistricts();
  const { data: countries = [] } = useCountries();
  const { data: provinces = [] } = useProvinces();
  const { data: departments = [] } = useDepartments();
  const { data: maritalStatuses = [] } = useMaritalStatuses();
  const { data: nationalities = [] } = useNationalities();

  const createMutation = useCreateLotTicket();
  const updateMutation = useUpdateLotTicket();
  const deleteMutation = useDeleteLotTicket();

  const availableLots = useMemo(
    () =>
      lots.filter(
        (lot) =>
          lot?.status === 'disponible' &&
          !lot?.person_name &&
          !lot?.uuid_contract &&
          lot?.is_active !== false
      ),
    [lots]
  );

  const availableLotsForDialog = useMemo(() => {
    if (!editMode || !selectedLotTicket?.uuid_lot) {
      return availableLots;
    }
    const selectedLot = lots.find((lot) => lot.uuid === selectedLotTicket.uuid_lot);
    if (!selectedLot) {
      return availableLots;
    }
    const alreadyIncluded = availableLots.some((lot) => lot.uuid === selectedLot.uuid);
    return alreadyIncluded ? availableLots : [selectedLot, ...availableLots];
  }, [availableLots, editMode, lots, selectedLotTicket?.uuid_lot]);

  const districtsMap = useMemo(() => new Map(districts.map((dist) => [dist.uuid, dist])), [districts]);
  const departmentsMap = useMemo(
    () => new Map(departments.map((dep) => [dep.uuid, dep])),
    [departments]
  );
  const provincesMap = useMemo(
    () => new Map(provinces.map((prov) => [prov.uuid, prov])),
    [provinces]
  );
  const countriesMap = useMemo(
    () => new Map(countries.map((country) => [country.uuid, country])),
    [countries]
  );

  const getLocationFromDistrict = (uuidDistrict) => {
    if (!uuidDistrict) return {};
    const district = districtsMap.get(uuidDistrict);
    const department = district ? departmentsMap.get(district.uuid_department) : null;
    const province = department ? provincesMap.get(department.uuid_province) : null;
    const country = province ? countriesMap.get(province.uuid_country) : null;
    return {
      uuid_department: department?.uuid || '',
      uuid_province: province?.uuid || '',
      uuid_country: country?.uuid || '',
    };
  };

  const {
    data: paginatedData,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: lotTickets,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterLotTickets,
  });

  const handleOpenDialog = () => {
    setSelectedLotTicket(INITIAL_LOT_TICKET_STATE);
    setEditMode(false);
    setErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedLotTicket((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setSelectedLotTicket((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'uuid_country') {
        next.uuid_province = '';
        next.uuid_department = '';
        next.uuid_district = '';
      }
      if (name === 'uuid_province') {
        next.uuid_department = '';
        next.uuid_district = '';
      }
      if (name === 'uuid_department') {
        next.uuid_district = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSave = () => {
    const validationErrors = validateLotTicket(selectedLotTicket);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const payload = buildPayload(selectedLotTicket);

    if (editMode) {
      updateMutation.mutate(
        { id: selectedLotTicket.uuid, lotTicketData: payload },
        {
          onSuccess: () => {
            handleCloseDialog();
          },
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          handleCloseDialog();
        },
      });
    }
  };

  const handleEdit = (lotTicket) => {
    const normalized = normalizeLotTicket(lotTicket);
    const location = getLocationFromDistrict(normalized.uuid_district);
    setSelectedLotTicket({
      ...normalized,
      uuid_country: location.uuid_country || '',
      uuid_province: location.uuid_province || '',
      uuid_department: location.uuid_department || '',
    });
    setErrors({});
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = (lotTicket) => {
    setLotTicketToDelete(lotTicket);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (lotTicketToDelete) {
      try {
        await deleteMutation.mutateAsync(lotTicketToDelete.uuid);
        setDeleteConfirmOpen(false);
        setLotTicketToDelete(null);
      } catch (error) {
        console.error('Error deleting lot ticket:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setLotTicketToDelete(null);
  };

  return {
    data: paginatedData,
    isLoading:
      isLotTicketsLoading ||
      isLotsLoading ||
      createMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedLotTicket,
    errors,
    lots,
    allotments,
    districts,
    countries,
    provinces,
    departments,
    maritalStatuses,
    nationalities,
    availableLots,
    availableLotsForDialog,
    deleteConfirmOpen,
    lotTicketToDelete,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
